/* ============================================================
   ET - Social Proof
   Swiper carousel + video popup with video-only navigation.
   IIFE, one instance per section id, re-inits on shopify:section:load,
   cleans up on shopify:section:unload.
   ============================================================ */
(function () {
  'use strict';

  var instances = {};

  function waitForSwiper(cb, tries) {
    tries = tries || 0;
    if (typeof window.Swiper !== 'undefined') {
      cb();
    } else if (tries < 120) {
      window.setTimeout(function () {
        waitForSwiper(cb, tries + 1);
      }, 50);
    }
  }

  function ETSocialProof(root) {
    this.root = root;
    this.sectionId = root.getAttribute('data-et-sp-id');
    this.swiperEl = root.querySelector('[data-et-sp-swiper]');
    this.popup = root.querySelector('[data-et-sp-popup]');
    this.video = root.querySelector('[data-et-sp-popup-video]');
    this.swiper = null;
    this.muted = false;
    this.lastFocused = null;

    // Ordered list of slide indices that have a video.
    this.videoIndices = [];
    this.slideData = {}; // index -> { src, poster, product }
    this.currentVideoIndex = null; // index into videoIndices

    this._onKeydown = this._onKeydown.bind(this);
    this._onVideoToggle = this._onVideoToggle.bind(this);
    this._onVideoEnded = this._onVideoEnded.bind(this);

    this._collectSlides();
    this._initSwiper();
    this._bindPopup();
    this._bindPlayButtons();
  }

  ETSocialProof.prototype._collectSlides = function () {
    var slides = this.root.querySelectorAll('[data-et-sp-index]');
    var i;
    for (i = 0; i < slides.length; i++) {
      var el = slides[i];
      var idx = parseInt(el.getAttribute('data-et-sp-index'), 10);
      var hasVideo = el.getAttribute('data-et-sp-has-video') === 'true';
      if (hasVideo) {
        this.videoIndices.push(idx);
        this.slideData[idx] = {
          src: el.getAttribute('data-et-sp-video-src') || '',
          poster: el.getAttribute('data-et-sp-poster') || '',
          product: null
        };
      }
    }
    this.videoIndices.sort(function (a, b) { return a - b; });

    // Merge product JSON (only emitted for video slides).
    var dataEl = this.root.querySelector('[data-et-sp-data]');
    if (dataEl) {
      try {
        var parsed = JSON.parse(dataEl.textContent);
        var j;
        for (j = 0; j < parsed.length; j++) {
          var rec = parsed[j];
          if (this.slideData[rec.index]) {
            this.slideData[rec.index].product = rec.hasProduct ? rec : null;
          }
        }
      } catch (e) {
        /* ignore malformed JSON */
      }
    }
  };

  ETSocialProof.prototype._initSwiper = function () {
    if (!this.swiperEl || typeof window.Swiper === 'undefined') return;

    var reduceMotion = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // swiper-bundle.min.js auto-registers all modules (Navigation, Scrollbar, FreeMode, etc.).
    // Base config = mobile: free-scroll so a swipe can stop anywhere (read a review
    // without it snapping to the next slide). The 750 breakpoint restores snapping on
    // desktop, where the arrows drive navigation.
    this.swiper = new window.Swiper(this.swiperEl, {
      slidesPerView: 'auto',
      spaceBetween: 16,
      speed: reduceMotion ? 0 : 400,
      watchOverflow: true,
      grabCursor: true,
      freeMode: {
        enabled: true,
        sticky: false,
        momentum: !reduceMotion,
        momentumRatio: 0.5
      },
      navigation: {
        nextEl: this.root.querySelector('[data-et-sp-next]'),
        prevEl: this.root.querySelector('[data-et-sp-prev]'),
        disabledClass: 'swiper-button-disabled'
      },
      scrollbar: {
        el: this.root.querySelector('[data-et-sp-scrollbar]'),
        draggable: true,
        dragClass: 'swiper-scrollbar-drag'
      },
      breakpoints: {
        750: {
          freeMode: { enabled: false }
        }
      }
    });
  };

  ETSocialProof.prototype._bindPlayButtons = function () {
    var self = this;
    // Event delegation on the track for all play buttons.
    if (!this.swiperEl) return;
    this.swiperEl.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-et-sp-play]');
      if (!btn || !self.swiperEl.contains(btn)) return;
      e.preventDefault();
      var idx = parseInt(btn.getAttribute('data-et-sp-play-index'), 10);
      self.openPopup(idx);
    });
  };

  ETSocialProof.prototype._bindPopup = function () {
    if (!this.popup) return;
    var self = this;

    this.popup.addEventListener('click', function (e) {
      if (e.target.closest('[data-et-sp-backdrop]')) {
        self.closePopup();
        return;
      }
      if (e.target.closest('[data-et-sp-popup-close]')) {
        self.closePopup();
        return;
      }
      if (e.target.closest('[data-et-sp-popup-playpause]')) {
        self._onVideoToggle();
        return;
      }
      if (e.target.closest('[data-et-sp-popup-mute]')) {
        self.toggleMute();
        return;
      }
      if (e.target.closest('[data-et-sp-popup-prev]')) {
        self.navVideo(-1);
        return;
      }
      if (e.target.closest('[data-et-sp-popup-next]')) {
        self.navVideo(1);
        return;
      }
    });

    // Clicking on the video toggles play/pause.
    if (this.video) {
      this.video.addEventListener('click', this._onVideoToggle);
      this.video.addEventListener('play', function () { self._syncPlayPause(); });
      this.video.addEventListener('pause', function () { self._syncPlayPause(); });
      this.video.addEventListener('ended', this._onVideoEnded);
    }
  };

  ETSocialProof.prototype.openPopup = function (slideIndex) {
    if (!this.popup || !this.video) return;
    var pos = this.videoIndices.indexOf(slideIndex);
    if (pos === -1) {
      // Not a video slide (defensive: photo slides have no play button anyway).
      return;
    }
    this.currentVideoIndex = pos;
    this.lastFocused = document.activeElement;

    this.popup.hidden = false;
    document.documentElement.classList.add('et-sp-scroll-lock');
    document.body.classList.add('et-sp-scroll-lock');

    document.addEventListener('keydown', this._onKeydown);

    // First open: autoplay WITH sound (the play-button click is the user gesture).
    this.muted = false;
    this._loadVideo(slideIndex, true);
    this._updateNavState();

    var closeBtn = this.popup.querySelector('[data-et-sp-popup-close]');
    if (closeBtn) closeBtn.focus();
  };

  ETSocialProof.prototype._loadVideo = function (slideIndex, autoplay) {
    var data = this.slideData[slideIndex];
    if (!data) return;

    this.video.pause();
    this.video.removeAttribute('src');
    // Clear any source children, then set src.
    while (this.video.firstChild) {
      this.video.removeChild(this.video.firstChild);
    }
    if (data.poster) this.video.setAttribute('poster', data.poster);
    if (data.src) this.video.setAttribute('src', data.src);

    this.video.muted = this.muted;
    this.video.load();

    this._renderPopupProduct(data.product);
    this._syncMuteIcon();

    if (autoplay) {
      var self = this;
      var playPromise = this.video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(function () {
          // Autoplay with sound blocked -> fall back to muted playback.
          self.muted = true;
          self.video.muted = true;
          self._syncMuteIcon();
          self.video.play().catch(function () {});
        });
      }
    }
    this._syncPlayPause();
  };

  ETSocialProof.prototype._renderPopupProduct = function (product) {
    var card = this.popup.querySelector('[data-et-sp-popup-product]');
    if (!card) return;
    if (!product || !product.hasProduct) {
      card.hidden = true;
      return;
    }
    card.hidden = false;
    card.setAttribute('href', product.url || '#');

    var img = card.querySelector('[data-et-sp-popup-product-img]');
    if (img) {
      if (product.img) {
        img.src = product.img;
        img.alt = product.title || '';
        img.hidden = false;
      } else {
        img.hidden = true;
      }
    }
    var title = card.querySelector('[data-et-sp-popup-product-title]');
    if (title) title.textContent = product.title || '';

    var price = card.querySelector('[data-et-sp-popup-product-price]');
    if (price) price.textContent = product.price || '';

    var compare = card.querySelector('[data-et-sp-popup-product-compare]');
    var badge = card.querySelector('[data-et-sp-popup-product-badge]');
    if (product.showCompare) {
      if (compare) {
        compare.textContent = product.compare || '';
        compare.hidden = false;
      }
      if (badge && product.discount > 0) {
        badge.textContent = '-' + product.discount + '%';
        badge.hidden = false;
      } else if (badge) {
        badge.hidden = true;
      }
    } else {
      if (compare) compare.hidden = true;
      if (badge) badge.hidden = true;
    }
  };

  ETSocialProof.prototype.navVideo = function (dir) {
    if (this.currentVideoIndex === null) return;
    var next = this.currentVideoIndex + dir;
    if (next < 0 || next >= this.videoIndices.length) return;
    this.currentVideoIndex = next;
    var slideIndex = this.videoIndices[next];
    this._loadVideo(slideIndex, true); // keep current mute state
    this._updateNavState();
    // Keep the carousel roughly in sync with the playing slide.
    if (this.swiper && typeof this.swiper.slideTo === 'function') {
      this.swiper.slideTo(slideIndex);
    }
  };

  ETSocialProof.prototype._updateNavState = function () {
    var prev = this.popup.querySelector('[data-et-sp-popup-prev]');
    var next = this.popup.querySelector('[data-et-sp-popup-next]');
    var single = this.videoIndices.length <= 1;
    if (prev) prev.disabled = single || this.currentVideoIndex <= 0;
    if (next) next.disabled = single || this.currentVideoIndex >= this.videoIndices.length - 1;
  };

  ETSocialProof.prototype._onVideoToggle = function (e) {
    if (e) e.preventDefault();
    if (!this.video) return;
    if (this.video.paused) {
      this.video.play().catch(function () {});
    } else {
      this.video.pause();
    }
  };

  ETSocialProof.prototype._onVideoEnded = function () {
    this._syncPlayPause();
  };

  ETSocialProof.prototype._syncPlayPause = function () {
    var btn = this.popup.querySelector('[data-et-sp-popup-playpause]');
    if (!btn) return;
    var pauseIcon = btn.querySelector('.et-social-proof__icon-pause');
    var playIcon = btn.querySelector('.et-social-proof__icon-play');
    var playing = this.video && !this.video.paused && !this.video.ended;
    if (pauseIcon) pauseIcon.hidden = !playing;
    if (playIcon) playIcon.hidden = playing;
    btn.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
  };

  ETSocialProof.prototype.toggleMute = function () {
    if (!this.video) return;
    this.muted = !this.muted;
    this.video.muted = this.muted;
    this._syncMuteIcon();
  };

  ETSocialProof.prototype._syncMuteIcon = function () {
    var btn = this.popup.querySelector('[data-et-sp-popup-mute]');
    if (!btn) return;
    var volIcon = btn.querySelector('.et-social-proof__icon-volume');
    var muteIcon = btn.querySelector('.et-social-proof__icon-muted');
    if (volIcon) volIcon.hidden = this.muted;
    if (muteIcon) muteIcon.hidden = !this.muted;
    btn.setAttribute('aria-label', this.muted ? 'Unmute video' : 'Mute video');
  };

  ETSocialProof.prototype.closePopup = function () {
    if (!this.popup) return;
    if (this.video) {
      this.video.pause();
      this.video.removeAttribute('src');
      while (this.video.firstChild) {
        this.video.removeChild(this.video.firstChild);
      }
      this.video.load();
    }
    this.popup.hidden = true;
    document.documentElement.classList.remove('et-sp-scroll-lock');
    document.body.classList.remove('et-sp-scroll-lock');
    document.removeEventListener('keydown', this._onKeydown);
    this.currentVideoIndex = null;
    if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
      this.lastFocused.focus();
    }
  };

  ETSocialProof.prototype._onKeydown = function (e) {
    // Ignore when the user is typing in a field (e.g. theme-editor inputs).
    var t = e.target;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) {
      return;
    }
    if (e.key === 'Escape' || e.keyCode === 27) {
      this.closePopup();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      this.navVideo(-1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      this.navVideo(1);
    }
  };

  ETSocialProof.prototype.destroy = function () {
    this.closePopup();
    if (this.swiper && typeof this.swiper.destroy === 'function') {
      this.swiper.destroy(true, true);
    }
    this.swiper = null;
  };

  /* --------------------------------------------------------------------------
     Swiper v11, loaded on approach instead of on page load.

     This used to be an inline <script> in the section that ran during HTML parse
     and injected both CDN assets into <head> unconditionally. Swiper was the
     heaviest single script on the mobile homepage: 1,880 ms CPU, a 942 ms long
     task, 1,326 ms of forced reflow, and this section is below the fold for
     everyone and display:none for the Control arm of A/B test HOME-002.

     Now the CDN assets are requested from an IntersectionObserver, 600px before
     the section reaches the viewport. That margin is deliberate: it gives the
     stylesheet time to land before the slides are on screen, so there is no
     layout shift when Swiper takes over. A display:none section has no layout
     box, never intersects, and costs nothing at all.
     -------------------------------------------------------------------------- */
  var SWIPER_CSS = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css';
  var SWIPER_JS = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js';

  function loadSwiperAssets() {
    if (window.__etSpSwiperLoading) return; /* once per page, however many sections */
    window.__etSpSwiperLoading = true;
    if (!document.querySelector('link[data-et-sp-swiper-css]')) {
      var l = document.createElement('link');
      l.rel = 'stylesheet';
      l.href = SWIPER_CSS;
      l.setAttribute('data-et-sp-swiper-css', '');
      document.head.appendChild(l);
    }
    if (typeof window.Swiper === 'undefined' && !document.querySelector('script[data-et-sp-swiper-js]')) {
      var s = document.createElement('script');
      s.src = SWIPER_JS;
      s.defer = true;
      s.setAttribute('data-et-sp-swiper-js', '');
      document.head.appendChild(s);
    }
  }

  function hydrate(root) {
    if (root.getAttribute('data-et-sp-ready') === 'true') return;
    loadSwiperAssets();
    var id = root.getAttribute('data-et-sp-id');
    waitForSwiper(function () {
      if (root.getAttribute('data-et-sp-ready') === 'true') return;
      root.setAttribute('data-et-sp-ready', 'true');
      instances[id] = new ETSocialProof(root);
    });
  }

  var observer = null;
  function getObserver() {
    if (observer) return observer;
    observer = new window.IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (!entries[i].isIntersecting) continue;
        observer.unobserve(entries[i].target);
        hydrate(entries[i].target);
      }
    }, { rootMargin: '600px 0px' });
    return observer;
  }

  function initRoot(root) {
    if (!root) return;
    if (root.getAttribute('data-et-sp-ready') === 'true') return;
    if (root.getAttribute('data-et-sp-observed') === 'true') return;

    /* In the theme editor a merchant must see the carousel without scrolling to
       it, and without IntersectionObserver there is nothing to defer with. */
    var designMode = !!(window.Shopify && window.Shopify.designMode);
    if (designMode || !('IntersectionObserver' in window)) {
      hydrate(root);
      return;
    }

    root.setAttribute('data-et-sp-observed', 'true');
    getObserver().observe(root);
  }

  function initAll(scope) {
    var container = scope || document;
    var roots = container.querySelectorAll('[data-et-sp]');
    var i;
    for (i = 0; i < roots.length; i++) {
      initRoot(roots[i]);
    }
  }

  // Inject scroll-lock style once.
  if (!document.getElementById('et-sp-scroll-lock-style')) {
    var styleEl = document.createElement('style');
    styleEl.id = 'et-sp-scroll-lock-style';
    styleEl.textContent = '.et-sp-scroll-lock{overflow:hidden!important;}';
    document.head.appendChild(styleEl);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector ? e.target.querySelector('[data-et-sp]') : null;
    if (root) {
      root.removeAttribute('data-et-sp-ready');
      root.removeAttribute('data-et-sp-observed');
      initRoot(root);
    }
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target.querySelector ? e.target.querySelector('[data-et-sp]') : null;
    if (root) {
      if (observer) observer.unobserve(root);
      var id = root.getAttribute('data-et-sp-id');
      if (instances[id]) {
        instances[id].destroy();
        delete instances[id];
      }
    }
  });
})();
